import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Truck,
  Building,
  Wrench,
  MapPin,
  Eye,
  MessageSquare,
  CheckCircle2,
  ArrowLeft,
  Phone,
  Mail,
  Calendar,
  Clock,
  IndianRupee,
  User,
  Info,
  Send,
  Package,
} from "lucide-react";
import api from "@/lib/api";
import { AuthContext } from "@/App";
import { toast } from "sonner";

const CATEGORIES = {
  equipment: {
    name: "Equipment",
    icon: Wrench,
    color: "bg-orange-500",
    lightColor: "bg-orange-50",
    textColor: "text-orange-600",
  },
  spaces: {
    name: "Spaces",
    icon: Building,
    color: "bg-blue-500",
    lightColor: "bg-blue-50",
    textColor: "text-blue-600",
  },
  transport: {
    name: "Transport",
    icon: Truck,
    color: "bg-green-500",
    lightColor: "bg-green-50",
    textColor: "text-green-600",
  }
};

export default function RentalDetail() {
  const { rentalId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [rental, setRental] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enquiryDialog, setEnquiryDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
    rental_duration: "",
    start_date: ""
  });

  useEffect(() => {
    fetchRental();
  }, [rentalId]);

  useEffect(() => {
    if (user && rental) {
      setEnquiryForm({
        name: user.name || "",
        email: user.email || "",
        mobile: user.mobile || "",
        message: `I am interested in renting "${rental.title}". Please provide more details about availability and terms.`,
        rental_duration: "",
        start_date: ""
      });
    }
  }, [user, rental]);

  const fetchRental = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/rentals/${rentalId}`);
      setRental(data);
    } catch (error) {
      console.error("Failed to fetch rental:", error);
      toast.error("Failed to load rental details");
      navigate("/rentals");
    } finally {
      setLoading(false);
    }
  };

  const handleEnquiry = () => {
    if (!user) {
      toast.error("Please login to send an enquiry");
      navigate("/auth");
      return;
    }
    setEnquiryDialog(true);
  };

  const handleSubmitEnquiry = async (e) => {
    e.preventDefault();
    if (!rental) return;

    setSubmitting(true);
    try {
      await api.post("/rental-enquiries", {
        rental_id: rental.id,
        name: enquiryForm.name,
        email: enquiryForm.email,
        mobile: enquiryForm.mobile,
        message: enquiryForm.message,
        rental_duration: enquiryForm.rental_duration,
        start_date: enquiryForm.start_date
      });
      toast.success("Enquiry sent successfully! The owner will contact you soon.");
      setEnquiryDialog(false);
    } catch (error) {
      console.error("Failed to submit enquiry:", error);
      toast.error(error.response?.data?.detail || "Failed to send enquiry");
    } finally {
      setSubmitting(false);
    }
  };

  const getCategoryStyles = (category) => {
    return CATEGORIES[category] || CATEGORIES.equipment;
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES[category];
    return cat ? cat.icon : Wrench;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  if (!rental) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Rental Not Found</h2>
          <p className="text-slate-600 mb-6">The rental you're looking for doesn't exist or has been removed.</p>
          <Link to="/rentals">
            <Button>Browse Rentals</Button>
          </Link>
        </div>
      </div>
    );
  }

  const catStyles = getCategoryStyles(rental.category);
  const Icon = getCategoryIcon(rental.category);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/rentals" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Rentals
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <Card className="overflow-hidden" data-testid="rental-images">
              {rental.images && rental.images.length > 0 ? (
                <img
                  src={rental.images[0]}
                  alt={rental.title}
                  className="w-full h-96 object-cover"
                />
              ) : (
                <div className={`w-full h-96 ${catStyles.lightColor} flex items-center justify-center`}>
                  <Icon className={`w-24 h-24 ${catStyles.textColor} opacity-40`} />
                </div>
              )}
            </Card>

            {/* Details Card */}
            <Card data-testid="rental-details">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`${catStyles.color} text-white`}>
                        {catStyles.name}
                      </Badge>
                      <Badge variant="outline">{rental.subcategory}</Badge>
                      {rental.verified && (
                        <Badge className="bg-emerald-500 text-white">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-2xl md:text-3xl">{rental.title}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Quick Stats */}
                <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {rental.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {rental.views} views
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-4 h-4" />
                    {rental.enquiries_count} enquiries
                  </span>
                  <span className={`flex items-center gap-1 font-medium ${
                    rental.availability === "Available" ? "text-emerald-600" :
                    rental.availability === "Limited" ? "text-amber-600" : "text-slate-600"
                  }`}>
                    <Clock className="w-4 h-4" />
                    {rental.availability}
                  </span>
                </div>

                {/* Description */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Description</h3>
                  <p className="text-slate-600 whitespace-pre-line">{rental.description}</p>
                </div>

                {/* Specifications */}
                {rental.specifications && (
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Specifications</h3>
                    <p className="text-slate-600 whitespace-pre-line">{rental.specifications}</p>
                  </div>
                )}

                {/* Additional Details */}
                <div className="grid sm:grid-cols-2 gap-4">
                  {rental.brand && (
                    <div className="flex items-center gap-2">
                      <Info className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">Brand: <strong className="text-slate-900">{rental.brand}</strong></span>
                    </div>
                  )}
                  {rental.model && (
                    <div className="flex items-center gap-2">
                      <Info className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">Model: <strong className="text-slate-900">{rental.model}</strong></span>
                    </div>
                  )}
                  {rental.year && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">Year: <strong className="text-slate-900">{rental.year}</strong></span>
                    </div>
                  )}
                  {rental.capacity && (
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">Capacity: <strong className="text-slate-900">{rental.capacity}</strong></span>
                    </div>
                  )}
                  {rental.min_rental_period && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">Min Rental: <strong className="text-slate-900">{rental.min_rental_period}</strong></span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pricing Card */}
            <Card data-testid="rental-pricing">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IndianRupee className="w-5 h-5" />
                  Rental Rates
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {rental.is_negotiable && !rental.hourly_rate && !rental.daily_rate && !rental.monthly_rate ? (
                  <div className="text-center py-4">
                    <p className="text-xl font-bold text-blue-600">Contact for Price</p>
                    <p className="text-sm text-slate-500 mt-1">Price is negotiable</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {rental.hourly_rate && (
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-slate-600">Hourly Rate</span>
                        <span className="text-xl font-bold text-slate-900">
                          ₹{rental.hourly_rate.toLocaleString('en-IN')}<span className="text-sm font-normal text-slate-500">/hr</span>
                        </span>
                      </div>
                    )}
                    {rental.daily_rate && (
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-slate-600">Daily Rate</span>
                        <span className="text-xl font-bold text-slate-900">
                          ₹{rental.daily_rate.toLocaleString('en-IN')}<span className="text-sm font-normal text-slate-500">/day</span>
                        </span>
                      </div>
                    )}
                    {rental.monthly_rate && (
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-slate-600">Monthly Rate</span>
                        <span className="text-xl font-bold text-slate-900">
                          ₹{rental.monthly_rate.toLocaleString('en-IN')}<span className="text-sm font-normal text-slate-500">/mo</span>
                        </span>
                      </div>
                    )}
                    {rental.is_negotiable && (
                      <div className="text-center">
                        <Badge variant="outline" className="text-green-600 border-green-200">
                          Price Negotiable
                        </Badge>
                      </div>
                    )}
                  </div>
                )}

                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg"
                  onClick={handleEnquiry}
                  data-testid="send-enquiry-btn"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Send Enquiry
                </Button>
              </CardContent>
            </Card>

            {/* Owner Card */}
            <Card data-testid="rental-owner">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Listed By
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-bold">
                    {rental.owner_name?.charAt(0)?.toUpperCase() || "O"}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{rental.owner_name}</p>
                    <p className="text-sm text-slate-500">Owner</p>
                  </div>
                </div>
                {rental.owner_mobile && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone className="w-4 h-4" />
                    <span>{rental.owner_mobile}</span>
                  </div>
                )}
                <p className="text-xs text-slate-500">
                  Listed on {new Date(rental.created_at).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </CardContent>
            </Card>

            {/* Safety Tips */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-amber-800 mb-2">Safety Tips</h3>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Verify the equipment/space before payment</li>
                  <li>• Always sign a rental agreement</li>
                  <li>• Check for insurance coverage</li>
                  <li>• Meet in safe, public locations</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Enquiry Dialog */}
      <Dialog open={enquiryDialog} onOpenChange={setEnquiryDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl">Send Enquiry</DialogTitle>
            <DialogDescription>
              <span className="text-base font-medium text-blue-600">
                {rental?.title}
              </span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitEnquiry} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="enquiry-name">Full Name</Label>
                <Input
                  id="enquiry-name"
                  value={enquiryForm.name}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, name: e.target.value })}
                  required
                  data-testid="enquiry-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="enquiry-mobile">Mobile</Label>
                <Input
                  id="enquiry-mobile"
                  type="tel"
                  value={enquiryForm.mobile}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, mobile: e.target.value })}
                  required
                  data-testid="enquiry-mobile-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="enquiry-email">Email</Label>
              <Input
                id="enquiry-email"
                type="email"
                value={enquiryForm.email}
                onChange={(e) => setEnquiryForm({ ...enquiryForm, email: e.target.value })}
                required
                data-testid="enquiry-email-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="enquiry-duration">Rental Duration</Label>
                <Input
                  id="enquiry-duration"
                  placeholder="e.g., 3 days, 1 month"
                  value={enquiryForm.rental_duration}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, rental_duration: e.target.value })}
                  data-testid="enquiry-duration-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="enquiry-start-date">Start Date</Label>
                <Input
                  id="enquiry-start-date"
                  type="date"
                  value={enquiryForm.start_date}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, start_date: e.target.value })}
                  data-testid="enquiry-start-date-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="enquiry-message">Message</Label>
              <Textarea
                id="enquiry-message"
                rows={4}
                value={enquiryForm.message}
                onChange={(e) => setEnquiryForm({ ...enquiryForm, message: e.target.value })}
                required
                data-testid="enquiry-message-input"
              />
            </div>

            {/* Pricing Summary */}
            {rental && (rental.daily_rate || rental.monthly_rate) && (
              <div className="bg-slate-50 p-4 rounded-lg space-y-2 text-sm">
                <p className="font-medium text-slate-900">Rate Summary</p>
                {rental.daily_rate && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Daily Rate:</span>
                    <span className="font-bold">₹{rental.daily_rate.toLocaleString('en-IN')}</span>
                  </div>
                )}
                {rental.monthly_rate && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Monthly Rate:</span>
                    <span className="font-bold">₹{rental.monthly_rate.toLocaleString('en-IN')}</span>
                  </div>
                )}
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={submitting}
              data-testid="submit-enquiry-btn"
            >
              {submitting ? "Sending..." : "Send Enquiry"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
